/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import overrides from '@instructure/translations/lib/canvas-lms/date-name-overrides.json'
import type {DateNames} from './strftime'

// Absent from every locale file; legacy strftime fell back to this too, so
// %p/%P have always been English-only.
const MERIDIAN: [string, string] = ['AM', 'PM']

type Category = 'month_names' | 'day_names' | 'abbr_month_names' | 'abbr_day_names'
type Overrides = Record<string, Partial<Record<Category, (string | null)[]>>>

// Canvas locale codes with no direct Intl/BCP-47 equivalent -- otherwise
// identical to their parent for CLDR purposes. Must match
// scripts/compute-date-name-overrides.js's INTL_LOCALE_MAP.
const INTL_LOCALE_MAP: Record<string, string> = {
  'da-x-k12': 'da',
  'nb-x-k12': 'nb',
  'sv-x-k12': 'sv',
}

const REF_YEAR = 2024 // 2024-01-07 is a Sunday

function intlMonths(tag: string, width: 'long' | 'short'): (string | null)[] {
  const fmt = new Intl.DateTimeFormat(tag, {month: width})
  const names: (string | null)[] = [null]
  for (let m = 0; m < 12; m++) names.push(fmt.format(new Date(REF_YEAR, m, 1)))
  return names
}

function intlDays(tag: string, width: 'long' | 'short'): string[] {
  const fmt = new Intl.DateTimeFormat(tag, {weekday: width})
  const names: string[] = []
  for (let d = 0; d < 7; d++) names.push(fmt.format(new Date(2024, 0, 7 + d)))
  return names
}

// Per-locale/per-category: Canvas's own data where it disagrees with Intl
// (packages/translations/lib/canvas-lms/date-name-overrides.json), Intl
// directly where it already agrees -- see scripts/compute-date-name-overrides.js.
function resolve(locale: string, category: Category): (string | null)[] | string[] | null {
  const override = (overrides as Overrides)[locale]?.[category]
  if (override != null) return override

  const tag = INTL_LOCALE_MAP[locale] || locale
  try {
    switch (category) {
      case 'month_names':
        return intlMonths(tag, 'long')
      case 'abbr_month_names':
        return intlMonths(tag, 'short')
      case 'day_names':
        return intlDays(tag, 'long')
      case 'abbr_day_names':
        return intlDays(tag, 'short')
    }
  } catch {
    return null
  }
}

function resolveWithEnglishFallback(locale: string, category: Category) {
  return resolve(locale, category) ?? resolve('en', category)
}

export function getLocaleDateNames(locale: string): DateNames {
  return {
    month_names: resolveWithEnglishFallback(locale, 'month_names') as (string | null)[],
    day_names: resolveWithEnglishFallback(locale, 'day_names') as string[],
    abbr_month_names: resolveWithEnglishFallback(locale, 'abbr_month_names') as (string | null)[],
    abbr_day_names: resolveWithEnglishFallback(locale, 'abbr_day_names') as string[],
    meridian: MERIDIAN,
  }
}
